import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Pencil, KeyRound, Copy, Mail } from "lucide-react";

export default function Assignees() {
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/assignees");
      setAssignees(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (a) => {
    setEditing(a);
    setNewName(a.name);
  };

  const saveRename = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/assignees/${encodeURIComponent(editing.email)}`, { name: newName });
      setAssignees((prev) => prev.map((a) => (a.email === editing.email ? { ...a, name: newName } : a)));
      toast.success("Assignee renamed. All their tasks are updated.");
      setEditing(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async (a) => {
    if (!window.confirm(`Reset ${a.name}'s board password? They'll set a new password on their next visit.`)) return;
    try {
      await api.post(`/assignees/${encodeURIComponent(a.email)}/reset-password`, {});
      toast.success("Board password cleared");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyShare = async (a) => {
    try {
      // fetch a task for that assignee to get share_token
      const { data } = await api.get("/tasks");
      const t = data.find((x) => x.assignee_email === a.email);
      if (!t) return toast.error("No tasks yet for this assignee");
      const url = `${window.location.origin}/board/${t.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Board link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Manage assignees
        </h1>
        <p className="text-slate-500 mt-2 mb-6">Rename an assignee (updates all their tasks) or reset their board password.</p>

        <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-500 text-sm text-center">Loading…</div>
          ) : assignees.length === 0 ? (
            <div className="p-8 text-slate-500 text-sm text-center">You haven't assigned any tasks yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="assignees-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Tasks</th>
                  <th className="px-4 py-3 font-semibold w-64"></th>
                </tr>
              </thead>
              <tbody>
                {assignees.map((a) => (
                  <tr key={a.email} className="border-b border-slate-100" data-testid={`assignee-row-${a.email}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                    <td className="px-4 py-3 text-slate-600 flex items-center gap-1.5">
                      <Mail size={12} className="text-slate-400" /> {a.email}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.count}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => copyShare(a)} data-testid={`copy-link-${a.email}`}>
                        <Copy size={13} className="mr-1.5" /> Link
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`rename-${a.email}`}>
                        <Pencil size={13} className="mr-1.5" /> Rename
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => resetPw(a)} data-testid={`reset-${a.email}`}>
                        <KeyRound size={13} className="mr-1.5" /> Reset pw
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-sm" data-testid="rename-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Rename assignee</DialogTitle>
              <p className="text-xs text-slate-500">All tasks assigned to this person will show the new name.</p>
            </DialogHeader>
            <form onSubmit={saveRename} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input required value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="rename-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="rename-save-btn">{busy ? "Saving…" : "Save"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
